import { Typography, Grid, TextField, Box } from "@mui/material";
import { useForm } from "react-hook-form";
import { useAppSelector } from "../../app/store/configureStore";
import { LoadingAnswersProps } from "../../app/models/Answers";
import RCI3ValidationSchema from "../../app/validations/RCI3Validations";
import { yupResolver } from "@hookform/resolvers/yup";

const Resource1StateChildAbuse: React.FC<LoadingAnswersProps> = ({
  answersData,
  onAnswerChange,
  onIsSubmittingChange,
  onIsValidChange,
}) => {
  const { user } = useAppSelector((state) => state.account);

  const defaultValues = {
    name:
      answersData.find((answer) => answer.systemCodeId === 10)?.answer || "",
    telephone:
      answersData.find((answer) => answer.systemCodeId === 11)?.answer || "",
    website:
      answersData.find((answer) => answer.systemCodeId === 12)?.answer || "",
    stateLegal:
      answersData.find((answer) => answer.systemCodeId === 13)?.answer || "",
    responsibilityReporting:
      answersData.find((answer) => answer.systemCodeId === 14)?.answer || "",
    stateLegalBilingual:
      answersData.find((answer) => answer.systemCodeId === 20)?.answer || "",
    responsibilityReportingBiligual:
      answersData.find((answer) => answer.systemCodeId === 21)?.answer || "",
  };
  const validationSchema = RCI3ValidationSchema({
    languageSpecialization: !!user?.languageSpecialization,
  });

  const {
    register,
    formState: { isSubmitting, errors, isValid  },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: "onTouched",
  });

  // Update isSubmitting in the parent component when it changes
  onIsSubmittingChange(isSubmitting);

  // Update isLoading in the parent component when it changes (if applicable)
  onIsValidChange(isValid);

  const handleNumberInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    const numericInput = input.replace(/\D/g, ""); // Remove non-numeric characters
    event.target.value = numericInput;
    const newValue = event.target.value;
    const systemCodeId = 11;
    onAnswerChange(systemCodeId, newValue);
  };

  return (
    <>
      <Grid item xs={12} px={3} pt={3}>
        <Typography variant="body1" fontWeight="bold" paragraph>
          RC I-3 State Child Abuse and Neglect Legal Requirements
        </Typography>

        <Typography variant="body1" paragraph>
          Provide a summary of the legal requirements in your state regarding
          child abuse and neglect (including contact information for the
          appropriate agency).
          {user?.languageSpecialization &&
            "You must provide this in both English and you second language."}
        </Typography>
        <Typography variant="body1" fontWeight="bold">
          State Child Welfare Agency
        </Typography>
      </Grid>
      <Box component="form">
        <Grid container spacing={2} px={3}>
          <Grid item sm={6} xs={12}>
            <TextField
              label="Name"
              {...register("name")}
              value={
                answersData.find((answer) => answer.systemCodeId === 10)
                  ?.answer || ""
              }
              onChange={(e) => {
                const newValue = e.target.value;
                const systemCodeId = 10;

                onAnswerChange(systemCodeId, newValue);
              }}
              fullWidth
              error={!!errors.name?.message}
              helperText={errors.name?.message}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <TextField
              label="Telephone"
              {...register("telephone")}
              value={
                answersData.find((answer) => answer.systemCodeId === 11)
                  ?.answer || ""
              }
              onChange={handleNumberInput}
              fullWidth
              error={!!errors.telephone?.message}
              helperText={errors.telephone?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid item sm={6} xs={12}>
              <TextField
                label="Website"
                {...register("website")}
                placeholder="https://www.website.com/"
                value={
                  answersData.find((answer) => answer.systemCodeId === 12)
                    ?.answer || ""
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  const systemCodeId = 12;

                  onAnswerChange(systemCodeId, newValue);
                }}
                fullWidth
                error={!!errors.website?.message}
                helperText={errors.website?.message}
              />
            </Grid>
          </Grid>
          <Grid item md={user?.languageSpecialization ? 6 : 12} sm={12}>
            <Typography variant="body1" fontWeight="bold" paragraph>
              State Legal & Mandatory Reporting Requirements
            </Typography>
            <Typography>
              When do you have to report suspected child abuse or neglect?
            </Typography>
            <TextField
              helperText={errors.stateLegal?.message}
              multiline
              rows={4}
              {...register("stateLegal")}
              value={
                answersData.find((answer) => answer.systemCodeId === 13)
                  ?.answer || ""
              }
              onChange={(e) => {
                const newValue = e.target.value;
                const systemCodeId = 13;
                if (newValue.length <= 1000) {
                  onAnswerChange(systemCodeId, newValue);
                }
              }}
              fullWidth
              error={!!errors.stateLegal?.message}
            />
            <Typography fontSize={13}>
              {defaultValues.stateLegal?.length}/1000
            </Typography>
          </Grid>
          {user?.languageSpecialization && (
            <Grid item md={6} sm={12}>
              <Typography variant="body1" fontWeight="bold" paragraph>
                (Respond in your second language)
              </Typography>
              <Typography>
                When do you have to report suspected child abuse or neglect?
              </Typography>
              <TextField
                placeholder=""
                multiline
                rows={4}
                {...register("stateLegalBilingual")}
                value={
                  answersData.find((answer) => answer.systemCodeId === 20)
                    ?.answer || ""
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  const systemCodeId = 20;

                  if (newValue.length <= 1000) {
                    onAnswerChange(systemCodeId, newValue);
                  }
                }}
                fullWidth
                error={!!errors.stateLegalBilingual?.message}
                helperText={errors.stateLegalBilingual?.message}
              />
              <Typography fontSize={13}>
                {defaultValues.stateLegalBilingual?.length}/1000
              </Typography>
            </Grid>
          )}
          <Grid item md={user?.languageSpecialization ? 6 : 12} sm={12}>
            <Typography variant="body1" fontWeight="bold" paragraph>
              Responsibility for reporting
            </Typography>
            <Typography>
              What is your program's policy regarding your responsibility for
              reporting child abuse and neglect?
            </Typography>
            <TextField
              placeholder="e.g. Based on my program's policy, my responsibility for reporting child abuse is..."
              multiline
              rows={4}
              {...register("responsibilityReporting")}
              value={
                answersData.find((answer) => answer.systemCodeId === 14)
                  ?.answer || ""
              }
              onChange={(e) => {
                const newValue = e.target.value;
                const systemCodeId = 14;

                if (newValue.length <= 1000) {
                  onAnswerChange(systemCodeId, newValue);
                }
              }}
              fullWidth
              error={!!errors.responsibilityReporting?.message}
              helperText={errors.responsibilityReporting?.message}
            />
            <Typography fontSize={13}>
              {defaultValues.responsibilityReporting?.length}/1000
            </Typography>
          </Grid>
          {user?.languageSpecialization && (
            <Grid item md={6} sm={12}>
              <Typography variant="body1" fontWeight="bold" paragraph>
                (Respond in your second language)
              </Typography>
              <Typography>
                What is your program's policy regarding your responsibility for
                reporting child abuse and neglect?
              </Typography>
              <TextField
                multiline
                rows={4}
                {...register("responsibilityReportingBiligual")}
                value={
                  answersData.find((answer) => answer.systemCodeId === 21)
                    ?.answer || ""
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  const systemCodeId = 21;

                  if (newValue.length <= 1000) {
                    onAnswerChange(systemCodeId, newValue);
                  }
                }}
                fullWidth
                error={!!errors.responsibilityReportingBiligual?.message}
                helperText={errors.responsibilityReportingBiligual?.message}
              />
              <Typography fontSize={13}>
                {defaultValues.responsibilityReportingBiligual?.length}/1000
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};

export default Resource1StateChildAbuse;
